import React from "react";
import {
  Check,
  ContentCopyOutlined,
  DownloadOutlined,
  Refresh,
  ReportOutlined,
} from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import { SEVERITIES } from "../../data/constants";
import { useToaster } from "../../hooks/useToaster";

export type WorkflowTerminalStatus = "completed" | "rejected" | "cancelled";

interface WorkflowTerminalStageProps {
  status: WorkflowTerminalStatus;
  reference: string;
  definitionName: string;
  /** What the rejected heading calls the case — "PIF" for the flagship, "Case" for everything
   *  else. Set by WorkflowRun from the definition. */
  subjectLabel: string;
  applicantName: string;
  employeeId: string | null;
  reason?: string;
  /** The reason the owner gave when closing a rejected case, shown under the approver's note. */
  closeReason?: string;
  /** Who closed the case with that reason. */
  closedBy?: string;
  decisionBy?: string;
  decidedAt?: string;
  readOnly?: boolean;
  onStartAnother: () => void;
  /** The case's share link, which Copy link copies (decision 6.5). */
  shareUrl?: string;
  /** Replaces the status's own heading and line - for a case closed on a Cancel stage that is not a
   *  rejection, such as a PIF all three directors approved whose employee couldn't be created. */
  closedAs?: { title: string; description: string };
}

const WorkflowTerminalStage: React.FC<WorkflowTerminalStageProps> = ({
  status,
  reference,
  definitionName,
  subjectLabel,
  applicantName,
  employeeId,
  reason,
  closeReason,
  closedBy,
  decisionBy,
  decidedAt,
  readOnly = false,
  onStartAnother,
  shareUrl,
  closedAs,
}) => {
  const showToaster = useToaster();
  const completed = status === "completed";
  const rejected = status === "rejected";
  const title = closedAs?.title ?? (completed
    ? "Signed & sent"
    : rejected
      ? `${subjectLabel} rejected`
      : "Case cancelled");
  const description = completed
    ? applicantName
      ? `The ${definitionName} case for ${applicantName} is fully approved${employeeId ? " and the employee record is provisioned" : ""}.`
      : `The ${definitionName} case is complete.`
    : rejected
      ? "The creator has been notified. The workflow stops here according to the reject gate."
      : "This case was cancelled and will not proceed.";
  const shownDescription = closedAs?.description ?? description;

  const handleCopyLink = () => {
    // Opening the link still requires sign-in and access to the case — it is never public.
    if (!shareUrl) return;
    void navigator.clipboard?.writeText(shareUrl);
    showToaster("Link copied", SEVERITIES.SUCCESS);
  };

  return (
    <Box className={`workflow-run__terminal workflow-run__terminal--${status}`}>
      <span className="workflow-run__terminal-icon">
        {completed ? <Check /> : <ReportOutlined />}
      </span>
      <Typography component="h2" className="workflow-run__terminal-title">
        {title}
      </Typography>
      <Typography className="workflow-run__terminal-copy">{shownDescription}</Typography>
      <Typography className="workflow-run__terminal-reference">{reference}</Typography>
      {completed && employeeId && (
        <Typography className="workflow-run__terminal-detail">
          New Enterprise Employee ID: <strong>{employeeId}</strong>
        </Typography>
      )}
      {(reason || decisionBy || closeReason) && (
        <Box className="workflow-run__rejection-reason">
          {/* Each note sits with the person who wrote it: the approver's reason, then the owner's
              close reason, laid out the same way. */}
          {(reason || decisionBy) && (
            <div className="workflow-run__reason-group">
              {reason && (
                <div>
                  <strong>Reason:</strong> {reason}
                </div>
              )}
              {decisionBy && (
                <div className="workflow-run__decision-stamp">
                  <strong>By:</strong> {decisionBy}
                  {decidedAt && ` · ${decidedAt}`}
                </div>
              )}
            </div>
          )}
          {closeReason && (
            <div className="workflow-run__reason-group">
              <div>
                <strong>Closed with:</strong> {closeReason}
              </div>
              {closedBy && (
                <div className="workflow-run__decision-stamp">
                  <strong>By:</strong> {closedBy}
                </div>
              )}
            </div>
          )}
        </Box>
      )}
      <Box className="workflow-run__terminal-actions">
        {completed && (
          <>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ContentCopyOutlined />}
              onClick={handleCopyLink}
              disabled={!shareUrl}
            >
              Copy link
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<DownloadOutlined />}
              disabled
            >
              Download PDF · v1 stub
            </Button>
          </>
        )}
        {!readOnly && (
          <Button variant="contained" startIcon={<Refresh />} onClick={onStartAnother}>
            Start another
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default WorkflowTerminalStage;
